import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import * as React from "react";
import { ArcElement, Chart, DoughnutController, Legend, Tooltip } from "chart.js";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import * as IdentityPermissionStore from "../identityPermissions/IdentityPermissionStore";
import LoadingSpinner from "../general/Loader";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import { Table } from "../components/Modal";
import { BreakdownItem, GetStats, GetStatsUsers, GroupOptionItem, OrganizationStats, StatsUserListResponse } from "./OverviewStore";

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

interface Filter {
    section: string;
    bucket: string;
    title: string;
}

const PAGE_SIZE = 25;

const isGroupBucket = (bucket: string) => bucket.startsWith("group") || bucket.startsWith("customtype-") || bucket === "type-member" || bucket === "type-owner";

const DonutCard = ({
    title,
    description,
    items,
    colors,
    selectedKeys,
    onSelect,
}: {
    title: string;
    description: string;
    items: BreakdownItem[];
    colors: string[];
    selectedKeys: string[];
    onSelect: (item: BreakdownItem) => void;
}) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    React.useEffect(() => {
        if (!canvasRef.current) return;

        const chart = new Chart(canvasRef.current, {
            type: "doughnut",
            data: {
                labels: items.map((i) => i.label),
                datasets: [
                    {
                        data: items.map((i) => i.count),
                        backgroundColor: colors,
                        borderColor: "#ffffff",
                        borderWidth: 2,
                        hoverOffset: 6,
                    },
                ],
            },
            options: {
                maintainAspectRatio: false,
                cutout: "62%",
                onClick: (_event, elements) => {
                    if (elements.length > 0) {
                        onSelect(items[elements[0].index]);
                    }
                },
                onHover: (event: any, elements) => {
                    if (event?.native?.target) {
                        event.native.target.style.cursor = elements.length > 0 ? "pointer" : "default";
                    }
                },
                plugins: {
                    legend: {
                        position: "bottom",
                        labels: { usePointStyle: true, boxWidth: 8, padding: 16, color: "#4B5563" },
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const total = items.reduce((sum, i) => sum + i.count, 0);
                                const value = context.parsed;
                                const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                                return ` ${value.toLocaleString()} (${percentage}%)`;
                            },
                        },
                    },
                },
            },
        });

        return () => chart.destroy();
    }, [JSON.stringify(items)]);

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
            <div className="relative h-64">
                <canvas ref={canvasRef}></canvas>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                {items.map((item, index) => (
                    <button
                        key={item.key}
                        onClick={() => onSelect(item)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${selectedKeys.includes(item.key) ? "bg-purple-100 text-purple-700 ring-1 ring-purple-300" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                    >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[index] }}></span>
                        {item.label}: {item.count.toLocaleString()}
                    </button>
                ))}
            </div>
        </div>
    );
};

const BreakdownBarsCard = ({
    title,
    description,
    items,
    color,
    selectedKeys,
    onSelect,
    fullWidth,
}: {
    title: string;
    description: string;
    items: BreakdownItem[];
    color: string;
    selectedKeys: string[];
    onSelect: (item: BreakdownItem) => void;
    fullWidth?: boolean;
}) => {
    const max = Math.max(1, ...items.map((i) => i.count));
    const total = items.reduce((sum, i) => sum + i.count, 0);

    return (
        <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${fullWidth ? "lg:col-span-2" : ""}`}>
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
            <div className={`space-y-4 ${items.length > 8 ? "max-h-96 overflow-y-auto pr-2" : ""}`}>
                {items.map((item) => {
                    const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                    return (
                        <button
                            key={item.key}
                            onClick={() => onSelect(item)}
                            className={`w-full text-left rounded-md p-2 -m-2 transition-colors ${selectedKeys.includes(item.key) ? "bg-purple-50 ring-1 ring-purple-300" : "hover:bg-gray-50"}`}
                        >
                            <div className="flex items-center justify-between text-sm mb-1.5">
                                <span className="font-medium text-gray-700">{item.label}</span>
                                <span className="text-gray-500 tabular-nums">
                                    {item.count.toLocaleString()} <span className="text-gray-400">({percentage}%)</span>
                                </span>
                            </div>
                            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${(item.count / max) * 100}%` }}></div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const StatCard = ({ icon, label, value, sub, accent, active, onClick }: { icon: IconProp; label: string; value: string; sub: string; accent: string; active: boolean; onClick: () => void }) => (
    <button
        onClick={onClick}
        className={`bg-white rounded-lg shadow-sm border p-5 text-left transition-all hover:shadow-md ${active ? "border-purple-500 ring-2 ring-purple-200" : "border-gray-200"}`}
    >
        <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
                <p className="text-sm text-gray-500 truncate">{label}</p>
                <p className="text-2xl font-semibold text-gray-800 mt-1 tabular-nums">{value}</p>
                <p className="text-xs text-gray-400 mt-1 truncate">{sub}</p>
            </div>
            <FontAwesomeIcon icon={icon} className={`text-3xl shrink-0 ${accent}`} />
        </div>
    </button>
);

const GroupPickerCard = ({ options, selectedKeys, onSelect }: { options: GroupOptionItem[]; selectedKeys: string[]; onSelect: (bucket: string, title: string) => void }) => {
    const [search, setSearch] = React.useState("");

    // Narrow the list to groups matching the active group-type filters (AND semantics)
    const enumTypeFilters = selectedKeys.filter((k) => k.startsWith("grouptype-")).map((k) => k.substring(10));
    const customTypeFilters = selectedKeys.filter((k) => k.startsWith("customtype-")).map((k) => k.substring(11));
    const typeFiltered = options.filter((g) => enumTypeFilters.every((t) => (g.type ?? "").toLowerCase() === t) && customTypeFilters.every((ct) => (g.groupCustomTypeID ?? "") === ct));
    const filtered = typeFiltered.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Filter by group</h3>
                <p className="text-sm text-gray-500">
                    Pick a specific group to filter users by
                    {(enumTypeFilters.length > 0 || customTypeFilters.length > 0) && ` — showing ${typeFiltered.length} of ${options.length} groups matching the selected type`}
                </p>
            </div>
            <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search groups..."
                className="w-full px-3 py-2 mb-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
            />
            <div className="max-h-64 overflow-y-auto space-y-1 -mx-2 px-2">
                {filtered.map((group) => {
                    const bucket = `group-${group.id}`;
                    const selected = selectedKeys.includes(bucket);
                    return (
                        <button
                            key={group.id}
                            onClick={() => onSelect(bucket, `Group: ${group.name}`)}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${selected ? "bg-purple-50 text-purple-700 ring-1 ring-purple-300" : "text-gray-700 hover:bg-gray-50"}`}
                        >
                            <span className="font-medium truncate">{group.name}</span>
                            <span className="flex items-center gap-2 ml-2 shrink-0">
                                <span className="text-xs text-gray-400">{group.customTypeName ?? group.type}</span>
                                <span className="text-gray-400 tabular-nums">{group.memberCount.toLocaleString()}</span>
                            </span>
                        </button>
                    );
                })}
                {filtered.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">{typeFiltered.length === 0 ? "No groups match the selected type filters." : `No groups match "${search}".`}</p>
                )}
            </div>
        </div>
    );
};

const UserList = ({ title, list, loading, page, onPage, section }: { title: string; list: StatsUserListResponse; loading: boolean; page: number; onPage: (page: number) => void; section: string }) => {
    const totalPages = list ? Math.max(1, Math.ceil(list.total / list.pageSize)) : 1;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                    <p className="text-sm text-gray-500">{list ? `${list.total.toLocaleString()} users` : "Loading..."}</p>
                </div>
                <FontAwesomeIcon icon={["fad", "users"]} className="text-2xl text-gray-300" />
            </div>

            {loading && (
                <div className="flex items-center justify-center py-16">
                    <LoadingSpinner />
                </div>
            )}

            {!loading && list && (
                <>
                    <div className="overflow-x-auto -mx-6">
                        <Table>
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{section === "group" ? "Groups" : "Memberships"}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {list.items.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="flex flex-wrap gap-1.5">
                                                {user.memberships.map((membership, index) => (
                                                    <span
                                                        key={index}
                                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                            membership.enabled === false
                                                                ? "bg-gray-100 text-gray-400"
                                                                : membership.detail === "Owner"
                                                                  ? "bg-purple-100 text-purple-700"
                                                                  : "bg-blue-50 text-blue-700"
                                                        }`}
                                                    >
                                                        {membership.label}
                                                        {membership.detail ? ` · ${membership.detail}` : ""}
                                                    </span>
                                                ))}
                                                {user.memberships.length === 0 && <span className="text-gray-400 text-xs">—</span>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {list.items.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-12 text-center text-sm text-gray-400">
                                            No users match these filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="flex items-center justify-between pt-4">
                        <p className="text-sm text-gray-500 tabular-nums">
                            Page {page} of {totalPages}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => onPage(page - 1)}
                                disabled={page <= 1}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => onPage(page + 1)}
                                disabled={page >= totalPages}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export const OverviewIndex = () => {
    const [organizationId, setOrganizationId] = React.useState("");
    const [allowed, setAllowed] = React.useState(false);
    const [stats, setStats] = React.useState(null as OrganizationStats | null);
    const [loading, setLoading] = React.useState(true);
    const [filters, setFilters] = React.useState([] as Filter[]);
    const [userList, setUserList] = React.useState(null as StatsUserListResponse | null);
    const [listLoading, setListLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);

    React.useEffect(() => {
        const subscriptionId = GetContext((context) => {
            setOrganizationId(context.currentOrganization.id);
        });

        const match = window.location.hash.match(/filters=([^&]*)/);
        if (match?.[1]) {
            const keys = match[1].split(",").filter(Boolean);
            if (keys.length > 0) {
                setFilters(keys.map((bucket) => ({ section: isGroupBucket(bucket) ? "group" : "org", bucket, title: "" })));
            }
        }

        return () => {
            UnsubscribeContext(subscriptionId);
        };
    }, []);

    React.useEffect(() => {
        if (organizationId) {
            IdentityPermissionStore.CheckCurrentUserHasPermission("manageOrganizations").then(setAllowed);
        }
    }, [organizationId]);

    React.useEffect(() => {
        if (!organizationId || !allowed) return;

        setLoading(true);
        setFilters((previous) => previous.filter((f) => !f.bucket.startsWith("customtype-") && (!f.bucket.startsWith("group-") || f.bucket.startsWith("grouptype-"))));
        setPage(1);
        GetStats()
            .then(setStats)
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    }, [organizationId, allowed]);

    React.useEffect(() => {
        window.history.replaceState(null, "", filters.length > 0 ? `#filters=${filters.map((f) => f.bucket).join(",")}` : window.location.pathname + window.location.search);
    }, [filters]);

    React.useEffect(() => {
        if (!organizationId || !allowed || loading || !stats) return;

        setListLoading(true);
        GetStatsUsers(
            filters.some((f) => isGroupBucket(f.bucket)) ? "group" : "org",
            filters.map((f) => f.bucket),
            page,
            PAGE_SIZE,
        )
            .then(setUserList)
            .catch(() => setUserList(null))
            .finally(() => setListLoading(false));
    }, [organizationId, allowed, loading, stats, filters, page]);

    const toggleFilter = (filter: Filter) => {
        setPage(1);
        setFilters((previous) => (previous.some((f) => f.bucket === filter.bucket) ? previous.filter((f) => f.bucket !== filter.bucket) : [...previous, filter]));
    };

    const labelFor = (bucket: string): string => {
        if (stats) {
            const breakdowns = [
                ...stats.organization.typeBreakdown,
                ...stats.organization.overlapBreakdown,
                ...stats.groups.membershipTypeBreakdown,
                ...stats.groups.overlapBreakdown,
                ...stats.groups.groupTypeBreakdown,
            ];
            const item = breakdowns.find((i) => i.key === bucket);
            if (item) return item.label;

            if (bucket.startsWith("group-")) {
                const group = stats.groups.groupOptions.find((g) => `group-${g.id}` === bucket);
                if (group) return `Group: ${group.name}`;
            }
        }

        const builtins: { [key: string]: string } = {
            all: "All users",
            active: "Active users",
            inactive: "Inactive users",
            multi: "Multiple memberships",
            "cross-org": "Also in other orgs",
            "group:all": "Group members",
            "group:active": "Active members",
            "group:inactive": "Inactive members",
            "group:external": "External members",
            "group:multi": "Multiple groups",
            "group:cross-org": "Other orgs' groups",
        };
        return builtins[bucket] ?? bucket;
    };

    if (!loading && !allowed) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <FontAwesomeIcon icon={["fad", "lock"]} className="text-4xl text-gray-300 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-700">Access denied</h2>
                    <p className="text-sm text-gray-500 mt-2">You need the manage organizations permission to view this page.</p>
                </div>
            </div>
        );
    }

    const org = stats?.organization;
    const groups = stats?.groups;
    const activePercentage = org && org.totalUsers > 0 ? Math.round((org.activeUsers / org.totalUsers) * 100) : 0;
    const selectedKeys = filters.map((f) => f.bucket);

    return (
        <div>
            <OrganizationContextSwitcher />

            <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={["fad", "chart-pie"]} className="text-3xl text-purple-500" />
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-800">Overview</h2>
                            <p className="text-sm text-gray-600">User and group membership insights for this organization — click charts, cards and groups to cross-filter the user list</p>
                        </div>
                    </div>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-24">
                        <LoadingSpinner />
                    </div>
                )}

                {!loading && !stats && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                        <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} className="text-4xl text-gray-300 mb-4" />
                        <h2 className="text-xl font-semibold text-gray-700">Could not load statistics</h2>
                        <p className="text-sm text-gray-500 mt-2">The statistics for this organization could not be retrieved.</p>
                    </div>
                )}

                {!loading && stats && org && groups && (
                    <>
                        {/* Organization memberships */}
                        <section className="space-y-4">
                            <div className="flex items-center gap-3">
                                <FontAwesomeIcon icon={["fad", "building"]} className="text-2xl text-blue-500" />
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800">Organization memberships</h3>
                                    <p className="text-sm text-gray-500">Who belongs to this organization and how</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <StatCard
                                    icon={["fad", "users"]}
                                    label="Users"
                                    value={org.totalUsers.toLocaleString()}
                                    sub={`${org.totalMemberships.toLocaleString()} memberships`}
                                    accent="text-purple-500"
                                    active={selectedKeys.includes("all")}
                                    onClick={() => toggleFilter({ section: "org", bucket: "all", title: "All users" })}
                                />
                                <StatCard
                                    icon={["fad", "user-check"]}
                                    label="Active"
                                    value={`${activePercentage}%`}
                                    sub={`${org.activeUsers.toLocaleString()} of ${org.totalUsers.toLocaleString()} users`}
                                    accent="text-green-500"
                                    active={selectedKeys.includes("active")}
                                    onClick={() => toggleFilter({ section: "org", bucket: "active", title: "Active users" })}
                                />
                                <StatCard
                                    icon={["fad", "layer-group"]}
                                    label="Multiple memberships"
                                    value={org.multiMembershipUsers.toLocaleString()}
                                    sub="users holding 2+ types"
                                    accent="text-blue-500"
                                    active={selectedKeys.includes("multi")}
                                    onClick={() => toggleFilter({ section: "org", bucket: "multi", title: "Multiple memberships" })}
                                />
                                <StatCard
                                    icon={["fad", "exchange"]}
                                    label="Also in other orgs"
                                    value={org.crossOrgUsers.toLocaleString()}
                                    sub={`across ${org.crossOrgOrganizationCount.toLocaleString()} organizations`}
                                    accent="text-amber-500"
                                    active={selectedKeys.includes("cross-org")}
                                    onClick={() => toggleFilter({ section: "org", bucket: "cross-org", title: "Also in other orgs" })}
                                />
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                <DonutCard
                                    title="Active vs inactive"
                                    description="Users by enabled membership state"
                                    items={[
                                        { key: "active", label: "Active", count: org.activeUsers },
                                        { key: "inactive", label: "Inactive", count: org.inactiveUsers },
                                    ]}
                                    colors={["#10B981", "#F87171"]}
                                    selectedKeys={selectedKeys}
                                    onSelect={(item) => toggleFilter({ section: "org", bucket: item.key, title: `${item.label} users` })}
                                />
                                <DonutCard
                                    title="Membership types"
                                    description="Memberships by type"
                                    items={org.typeBreakdown}
                                    colors={["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B"]}
                                    selectedKeys={selectedKeys}
                                    onSelect={(item) => toggleFilter({ section: "org", bucket: item.key, title: `${item.label} memberships` })}
                                />
                                <BreakdownBarsCard
                                    title="Membership overlap"
                                    description="Users by number of membership types held"
                                    items={org.overlapBreakdown}
                                    color="bg-blue-500"
                                    selectedKeys={selectedKeys}
                                    onSelect={(item) => toggleFilter({ section: "org", bucket: item.key, title: `Users with ${item.label} membership${item.label === "1" ? "" : "s"}` })}
                                />
                            </div>
                        </section>

                        {/* Group memberships */}
                        <section className="space-y-4">
                            <div className="flex items-center gap-3">
                                <FontAwesomeIcon icon={["fad", "users-cog"]} className="text-2xl text-indigo-500" />
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800">Group memberships</h3>
                                    <p className="text-sm text-gray-500">How users are organized into groups</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <StatCard
                                    icon={["fad", "users"]}
                                    label="Group members"
                                    value={groups.totalMembers.toLocaleString()}
                                    sub={`${groups.totalMemberships.toLocaleString()} memberships`}
                                    accent="text-indigo-500"
                                    active={selectedKeys.includes("group:all")}
                                    onClick={() => toggleFilter({ section: "group", bucket: "group:all", title: "Group members" })}
                                />
                                <StatCard
                                    icon={["fad", "user-shield"]}
                                    label="External members"
                                    value={groups.externalMembers.toLocaleString()}
                                    sub="no membership in this org"
                                    accent="text-amber-500"
                                    active={selectedKeys.includes("group:external")}
                                    onClick={() => toggleFilter({ section: "group", bucket: "group:external", title: "External members" })}
                                />
                                <StatCard
                                    icon={["fad", "layer-group"]}
                                    label="Multiple groups"
                                    value={groups.multiGroupUsers.toLocaleString()}
                                    sub="users in 2+ groups"
                                    accent="text-blue-500"
                                    active={selectedKeys.includes("group:multi")}
                                    onClick={() => toggleFilter({ section: "group", bucket: "group:multi", title: "Multiple groups" })}
                                />
                                <StatCard
                                    icon={["fad", "exchange"]}
                                    label="Also in other orgs' groups"
                                    value={groups.crossOrgUsers.toLocaleString()}
                                    sub="members of groups elsewhere"
                                    accent="text-green-500"
                                    active={selectedKeys.includes("group:cross-org")}
                                    onClick={() => toggleFilter({ section: "group", bucket: "group:cross-org", title: "Other orgs' groups" })}
                                />
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <DonutCard
                                    title="Member state"
                                    description="Group members by organization membership state"
                                    items={[
                                        { key: "group:active", label: "Active", count: groups.activeMembers },
                                        { key: "group:inactive", label: "Inactive", count: groups.inactiveMembers },
                                        { key: "group:external", label: "External", count: groups.externalMembers },
                                    ]}
                                    colors={["#10B981", "#F87171", "#F59E0B"]}
                                    selectedKeys={selectedKeys}
                                    onSelect={(item) => toggleFilter({ section: "group", bucket: item.key, title: `${item.label} members` })}
                                />
                                <DonutCard
                                    title="Membership types"
                                    description="Group memberships by role"
                                    items={groups.membershipTypeBreakdown}
                                    colors={["#3B82F6", "#8B5CF6"]}
                                    selectedKeys={selectedKeys}
                                    onSelect={(item) => toggleFilter({ section: "group", bucket: item.key, title: `Group ${item.label.toLowerCase()}s` })}
                                />
                                <BreakdownBarsCard
                                    title="Group overlap"
                                    description="Users by number of groups joined"
                                    items={groups.overlapBreakdown}
                                    color="bg-indigo-500"
                                    selectedKeys={selectedKeys}
                                    onSelect={(item) => toggleFilter({ section: "group", bucket: item.key, title: `Users in ${item.label} group${item.label === "1" ? "" : "s"}` })}
                                    fullWidth
                                />
                                <BreakdownBarsCard
                                    title="Members by group type"
                                    description="Group memberships per group type"
                                    items={groups.groupTypeBreakdown}
                                    color="bg-emerald-500"
                                    selectedKeys={selectedKeys}
                                    onSelect={(item) => toggleFilter({ section: "group", bucket: item.key, title: `${item.label} groups` })}
                                />
                                <GroupPickerCard options={groups.groupOptions} selectedKeys={selectedKeys} onSelect={(bucket, title) => toggleFilter({ section: "group", bucket, title })} />
                            </div>
                        </section>

                        {/* Active filters */}
                        {filters.length > 0 && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex items-center flex-wrap gap-2">
                                <span className="text-sm font-medium text-gray-500 mr-1">
                                    <FontAwesomeIcon icon={["fad", "filter"]} className="mr-2 text-purple-400" />
                                    Filters
                                </span>
                                {filters.map((filter) => (
                                    <span key={filter.bucket} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                        {filter.title || labelFor(filter.bucket)}
                                        <button onClick={() => toggleFilter(filter)} className="hover:text-purple-900" aria-label={`Remove filter ${filter.title || filter.bucket}`}>
                                            <FontAwesomeIcon icon={["fad", "times"]} />
                                        </button>
                                    </span>
                                ))}
                                <button
                                    onClick={() => {
                                        setPage(1);
                                        setFilters([]);
                                    }}
                                    className="ml-auto text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    Clear all
                                </button>
                            </div>
                        )}

                        {/* User results — always visible, cross-filtered */}
                        <UserList
                            title={filters.length > 0 ? filters.map((f) => f.title || labelFor(f.bucket)).join(" + ") : "All users"}
                            list={userList}
                            loading={listLoading}
                            page={page}
                            onPage={setPage}
                            section={filters.some((f) => isGroupBucket(f.bucket)) ? "group" : "org"}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
